import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../environments/environment';
import {
  CaasComponent,
  CaasError404,
  CaasLayout,
  CaasMainSection,
  CaasStage,
} from '../../../hdi-components/src/shared/interfaces/caas/caas.interface';
import { CaasContentService } from './caas-content.service';
import { resolutionConfig } from './resolution-config';
import { News } from '../../../hdi-components/src/shared/interfaces/news.interface';
import { PodcastItems } from 'hdi-components';

@Injectable({
  providedIn: 'root',
})
export class ImageService {
  private readonly imageURL = environment.caasMedia;
  private readonly imageUrlsSubject = new BehaviorSubject<
    { resolution: string; uid: string; url: string; alt?: string }[]
  >([]);
  imageUrls$ = this.imageUrlsSubject.asObservable();

  constructor(private caasContentServices: CaasContentService) {}

  /**
   * Retrieves all image URLs and their corresponding uids.
   */
  getAllImageUrls(): Observable<
    { resolution: string; uid: string; url: string }[]
  > {
    return this.caasContentServices.getMedia().pipe(
      map((mediaItems: CaasLayout[]) => {
        const imageUrls: { resolution: string; uid: string; url: string }[] =
          [];

        mediaItems.forEach((mediaItem) => {
          if (mediaItem.resolutionsMetaData) {
            const resolutions = mediaItem.resolutionsMetaData;
            const uid = mediaItem.uid;

            if (uid) {
              Object.keys(resolutions).forEach((resolution) => {
                const imageUrl = `${this.imageURL}${uid}-${resolution}`;

                imageUrls.push({
                  resolution,
                  uid,
                  url: imageUrl,
                });
              });
            }
          }
        });

        this.imageUrlsSubject.next(imageUrls);
        return imageUrls;
      }),
      catchError((error) => {
        console.error('Failed to fetch image URLs:', error);
        return throwError(() => new Error('Error fetching image URLs.'));
      })
    );
  }

  replaceStageImages(CaasStage: CaasStage): void {
    if (!CaasStage || !CaasStage.template || !CaasStage.formData) {
      // console.warn('ImageService > Debug: Invalid CaasStage input:', CaasStage);
      return;
    }

    if (CaasStage.template.uid === 'st_stage_home') {
      if (CaasStage.formData.st_teaser1_image?.value?.uid) {
        CaasStage.formData.st_teaser1_image.value.url = this.buildImageURL(
          CaasStage.formData.st_teaser1_image.value.uid,
          CaasStage.template.uid
        );
      } else {
        console.warn(
          'ImageService > Debug: Missing st_teaser1_image UID for stage_start'
        );
      }

      if (CaasStage.formData.st_teaser3_image?.value?.uid) {
        CaasStage.formData.st_teaser3_image.value.url = this.buildImageURL(
          CaasStage.formData.st_teaser3_image.value.uid,
          CaasStage.template.uid
        );
      } else {
        console.warn(
          'ImageService > Debug: Missing st_teaser3_image UID for stage_start'
        );
      }
    } else if (CaasStage.template.uid === 'st_stage') {
      if (Array.isArray(CaasStage.formData.st_images?.value)) {
        CaasStage.formData.st_images?.value.forEach((image, index) => {
          if (image.formData?.st_image?.value?.uid) {
            image.formData.st_image.value.url = this.buildImageURL(
              image.formData.st_image.value.uid,
              CaasStage.template.uid
            );
          } else {
            console.warn(
              `ImageService > Debug: Missing UID in st_images[${index}] for st_stage`
            );
          }
        });
      } else {
        console.warn(
          'ImageService > Debug: st_images is missing or not an array in st_stage.'
        );
      }
    }
  }

  /**
   * Replace the image URLs in the content with the new image URLs based on matching UIDs and resolutions.
   */
  replaceMainImages(content: CaasMainSection[]): void {
    content.map((section) => {
      if (section.formData?.st_items?.value) {
        this.ReplaceComponentImages(section.formData.st_items?.value);
      } else {
        console.warn('ImageService: No st_items found for section:', section);
      }
    });
  }

  ReplaceComponentImages(components: CaasComponent[] = []): void {
    components.forEach((component) => {
      if (
        (!component || !component.template || !component.template.uid) &&
        component.fsType !== 'Record'
      ) {
        console.warn('Skipping invalid component:', component);
        return;
      }

      const {
        st_image,
        st_images,
        st_items,
        st_elements,
        st_featured_image,
        st_image_mobile,
      } = component.formData || {};

      // Handle the 'st_teasers' or 'st_teaser' template with horizontal layout
      if (
        (component?.template?.uid === 'st_teasers' ||
          component?.template?.uid === 'st_teaser') &&
        component?.formData?.st_layout?.value?.identifier === 'horizontal'
      ) {
        // Replace the image URL for the st_image field if it exists
        if (st_image?.value?.uid) {
          st_image.value.url = this.buildImageURL(
            st_image.value.uid,
            'st_teasers_quer'
          );
        }

        // Handle st_items if it exists
        if (Array.isArray(st_items?.value)) {
          st_items.value.forEach((item, index) => {
            if (item.formData?.st_image?.value?.uid) {
              item.formData.st_image.value.url = this.buildImageURL(
                item.formData.st_image.value.uid,
                'st_teasers_quer'
              );
            } else {
              console.warn(`st_image UID missing in st_items[${index}]`);
            }
          });
        }
      } else {
        // Process single image fields (st_image, st_featured_image, st_image_mobile)
        [st_image, st_featured_image, st_image_mobile].forEach((imageField) => {
          if (imageField?.value?.uid) {
            imageField.value.url = this.buildImageURL(
              imageField.value.uid,
              component.template.uid
            );
          }
        });

        // Process st_images if it exists and is an array
        if (Array.isArray(st_images?.value)) {
          st_images.value.forEach((image, index) => {
            const nestedImage = image.formData?.st_image;
            const nestedMobileImage = image.formData?.st_image_mobile;

            if (nestedImage?.value?.uid) {
              nestedImage.value.url = this.buildImageURL(
                nestedImage.value.uid,
                component.template.uid
              );
              // console.log(
              //   `Processed nested image in st_images for component: ${component.template.uid}, index: ${index}, URL: ${nestedImage.value.url}`
              // );
            } else {
              console.warn(
                `ImageService: Missing or invalid nested image UID in st_images, index: ${index}, component: ${component.template.uid}`
              );
            }

            if (nestedMobileImage?.value?.uid) {
              nestedMobileImage.value.url = this.buildImageURL(
                nestedMobileImage.value.uid,
                component.template.uid
              );
            } else {
              console.warn(
                `ImageService: Missing or invalid nested mobile image UID in st_images, index: ${index}, component: ${component.template.uid}`
              );
            }
          });
        }

        // Process nested components (st_items, st_elements)
        [st_items, st_elements].forEach((itemField) => {
          if (Array.isArray(itemField?.value)) {
            // console.log(
            //   `Processing nested components in field: ${itemField.name}, component: ${component.template.uid}`
            // );
            this.ReplaceComponentImages(itemField.value as CaasComponent[]);
          }
        });
      }
    });
  }

  replaceErrorImage = (errorContent: CaasError404) => {
    errorContent.formData.st_image.value.url = this.buildImageURL(
      errorContent.formData.st_image.value.uid,
      errorContent.formData.st_image.value.url
    );
  };

  replaceNewsImages(news: News[]): void {
    news.forEach((article) => {
      if (article.imageSrc && article.imageUID) {
        article.imageSrc = this.buildImageURL(article.imageUID, 'st_teaser');
      } else {
        console.warn(
          'ImageService: Missing image src or identifier in news article:',
          article
        );
      }
    });
  }

  replacePodcastsImages(podcasts: PodcastItems[]): void {
    if (!Array.isArray(podcasts) || podcasts.length === 0) {
      console.warn('ImageService: No podcast data provided or invalid input.');
      return;
    }

    podcasts.forEach((podcast) => {
      if (podcast.image && podcast.imageUID) {
        podcast.image = this.buildImageURL(podcast.imageUID, 'st_podcasts');
      } else {
        console.warn(
          'ImageService: Missing image or imageUID for podcast episode:',
          podcast
        );
      }

      if (!podcast.timestamp) {
        console.warn(
          'ImageService: Missing timestamp for podcast episode:',
          podcast.title
        );
      }
    });
  }

  /**
   * Build an image URL based on the UID and component
   * @param uid The unique identifier for the image
   * @param componentUid The unique identifier for the component
   * @returns The image URL
   */
  buildImageURL(uid: string, componentUid: string): string {
    if (!uid) {
      console.warn(
        'ImageService: Missing UID in buildImageURL for component:',
        componentUid
      );
      return '';
    }

    const resolutions = this.getResolutionForComponent(componentUid);

    if (!resolutions || resolutions.length === 0) {
      console.warn(
        'ImageService: No resolutions found for component UID:',
        componentUid
      );
      return '';
    }

    // If only one resolution exists, return its URL
    if (resolutions.length === 1) {
      const resolution = resolutions[0].resolution;

      const newUrl = `${this.imageURL}${uid}-${resolution}`;
      this.imageUrlsSubject.next([
        ...this.imageUrlsSubject.value,
        { resolution, uid, url: newUrl },
      ]);

      return newUrl;
    }

    // Build a srcset string for multiple resolutions
    const srcsetArray = resolutions.map((res) => {
      const imageUrl = `${this.imageURL}${uid}-${res.resolution}`;
      const mediaQuery =
        res.max === Infinity
          ? `(min-width: ${res.min}px)` // Kein max-width
          : `(min-width: ${res.min}px) and (max-width: ${res.max}px)`;

      return {
        src: imageUrl,
        media: mediaQuery,
      };
    });

    return JSON.stringify(srcsetArray);
  }

  /**
   * Fetches the resolutions for a given component based on the `resolutionConfig`.
   */
  getResolutionForComponent(
    componentUid: string
  ): { resolution: string; min: number; max: number }[] {
    const componentResolutions = resolutionConfig[componentUid];

    if (!componentResolutions) {
      console.warn(
        'ImageService: No resolution config found for component UID:',
        componentUid,
        'Returning default resolution'
      );
      return resolutionConfig['default'];
    }

    return componentResolutions;
  }
}
